import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { queryProducts } from '@/data/productHelpers';
import catalogProducts from '@/data/products';
import { supportsHoverVideo } from '../constants/hoverVideoProducts';

const CATEGORY_LABELS = {
  novedades: 'Novedades',
  '2026': '2026',
  'sport-club': 'Sport Club',
  camisetas: 'Camisetas',
  polos: 'Polos',
  sueteres: 'Suéteres',
  'suéteres': 'Suéteres',
  hombre: 'Hombre',
  mujer: 'Mujer',
  accesorios: 'Accesorios',
};

const CATEGORY_NAV_ITEMS = [
  ['2026', CATEGORY_LABELS['2026']],
  ['limited-editions', 'Limited Editions'],
  ['novedades', CATEGORY_LABELS.novedades],
  ['sport-club', CATEGORY_LABELS['sport-club']],
  ['camisetas', CATEGORY_LABELS.camisetas],
  ['polos', CATEGORY_LABELS.polos],
  ['sueteres', CATEGORY_LABELS.sueteres],
  ['hombre', CATEGORY_LABELS.hombre],
  ['mujer', CATEGORY_LABELS.mujer],
  ['accesorios', CATEGORY_LABELS.accesorios],
];

const COLLECTION_LABELS = {
  roma: 'Roma',
  atelier: 'Atelier',
  limited: 'Piezas Limitadas',
};

const HEADER_LABEL_OVERRIDES = {
  hombre: 'UOMO',
  mujer: 'DONNA',
};

const MUJER_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_6fc96d8f-cb6c-4beb-8fea-5ecb3f3ddc7f/artifacts/udlexuwa_hf_20260222_200211_c5c76655-5b93-4052-adc1-45fea5a9cdc5.jpg";
const MUJER_CINEMATIC_VIDEO = "https://customer-assets.emergentagent.com/job_14c68bcb-ef5d-44c9-b883-bd8d392c855c/artifacts/b92rzs04_ANUNCIO%20TAMBVRINI%203.mov";
const HOMBRE_CINEMATIC_VIDEO = "https://customer-assets.emergentagent.com/job_14c68bcb-ef5d-44c9-b883-bd8d392c855c/artifacts/nqiyik78_video%20final%20tambvrini%202.mov";

const HOMBRE_CAMPAIGN_IMAGE = "https://customer-assets.emergentagent.com/job_6fc96d8f-cb6c-4beb-8fea-5ecb3f3ddc7f/artifacts/91b5s9c4_HOMBRE.jpg";
const COLLECTION_2026_HEADER_VIDEO = '/videos/tambvrini-intro-loop.mp4';
const ACCESSORIES_HEADER_IMAGE = '/images/header-gafas.jpeg';
const COLLECTION_2026_EDITORIAL_IMAGE = '/images/2026.PNG';

function CategoryHeaderMedia({ type, src, alt, testId }) {
  return (
    <div data-testid={`${testId}-banner`} className="mb-12">
      <div className="overflow-hidden rounded-[22px] shadow-[0_18px_46px_rgba(0,0,0,0.08)]">
        {type === 'video' ? (
          <video
            data-testid={`${testId}-video`}
            src={src}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-auto max-w-full object-contain object-center"
            aria-label={alt}
          />
        ) : (
          <img
            data-testid={`${testId}-image`}
            src={src}
            alt={alt}
            loading="lazy"
            className="w-full h-auto object-cover object-center"
          />
        )}
      </div>
    </div>
  );
}

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [introProgress, setIntroProgress] = useState(0);
  const [introVisible, setIntroVisible] = useState(false);
  const introRef = useRef(null);
  const videoRef = useRef(null);

  const category = searchParams.get('category') || searchParams.get('filter') || '';
  const normalizedCategory = category === 'marroquineria'
    ? 'accesorios'
    : category === 'suéteres'
      ? 'sueteres'
      : category;
  const gender = searchParams.get('gender') || '';
  const collection = searchParams.get('collection') || '';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || '';
  const page = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [normalizedCategory, gender, collection, search, sort, page]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const result = queryProducts({
          category: normalizedCategory || undefined,
          gender: gender || undefined,
          collection: collection || undefined,
          search: search || undefined,
          sort: sort || undefined,
          page,
          limit: 20,
        });
        setProducts(result.products);
        setTotal(result.total);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [normalizedCategory, gender, collection, search, sort, page]);

  const getTitle = () => {
    if (search) return `Resultados: "${search}"`;
    if (gender) return HEADER_LABEL_OVERRIDES[gender] || CATEGORY_LABELS[gender] || gender;
    if (normalizedCategory) return HEADER_LABEL_OVERRIDES[normalizedCategory] || CATEGORY_LABELS[normalizedCategory] || normalizedCategory;
    if (collection) return COLLECTION_LABELS[collection] || collection;
    return 'Tienda';
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const hasActiveFilters = category || gender || collection || search;

  const filteredProducts = gender === 'hombre'
    ? products.filter((p) => p.product_id !== 'bolso-monograma-tambvrini' && p.product_id !== 'americana-umbra')
    : gender === 'mujer'
      ? products.filter((p) => p.product_id !== 'traje-monograma-tambvrini')
      : products;

  const isMensView = gender === 'hombre';
  const isWomenView = gender === 'mujer';
  const isAccessoriesView = normalizedCategory === 'accesorios';
  const mujerProducts = useMemo(() => {
    if (!isWomenView) return filteredProducts;
    const poloAureus = catalogProducts.find((p) => p.product_id === 'polo-aureus');
    const poloPatricius = catalogProducts.find((p) => p.product_id === 'polo-patricius');
    const ordered = [...filteredProducts];
    let imperiumIndex = -1;
    let umbraIndex = -1;
    let hasAureus = false;
    let hasPatricius = false;

    for (let index = 0; index < ordered.length; index += 1) {
      const productId = ordered[index].product_id;
      if (imperiumIndex === -1 && productId === 'camiseta-imperium') imperiumIndex = index;
      if (umbraIndex === -1 && productId === 'americana-umbra') umbraIndex = index;
      if (!hasAureus && productId === 'polo-aureus') hasAureus = true;
      if (!hasPatricius && productId === 'polo-patricius') hasPatricius = true;
      if (imperiumIndex !== -1 && umbraIndex !== -1 && hasAureus && hasPatricius) {
        break;
      }
    }

    if (poloAureus && !hasAureus && imperiumIndex !== -1) {
      ordered.splice(imperiumIndex, 0, poloAureus);
      if (umbraIndex >= imperiumIndex) {
        umbraIndex += 1;
      }
    }

    if (poloPatricius && !hasPatricius && umbraIndex !== -1) {
      ordered.splice(umbraIndex + 1, 0, poloPatricius);
    }

    return ordered;
  }, [filteredProducts, isWomenView]);
  const gridProducts = isWomenView ? mujerProducts : filteredProducts;
  const displayTotal = isMensView ? filteredProducts.length : isWomenView ? mujerProducts.length : total;
  const isCinematicView = isWomenView || isMensView;
  const mensFirst = isMensView ? filteredProducts.slice(0, 4) : filteredProducts;
  const mensRest = isMensView ? filteredProducts.slice(4) : [];
  const mujerImperiumIndex = isWomenView
    ? mujerProducts.findIndex((p) => p.product_id === 'camiseta-imperium')
    : -1;
  const mujerGridItems = isWomenView && mujerImperiumIndex !== -1
    ? [
        ...mujerProducts.slice(0, mujerImperiumIndex + 1),
        { type: 'editorial', id: 'mujer-editorial' },
        ...mujerProducts.slice(mujerImperiumIndex + 1),
      ]
    : gridProducts;
  const isCollection2026View = normalizedCategory === '2026';
  const collection2026CaptainIndex = isCollection2026View
    ? gridProducts.findIndex((p) => p.product_id === 'sueter-captain')
    : -1;
  const collection2026GridItems = isCollection2026View && collection2026CaptainIndex !== -1
    ? [
        ...gridProducts.slice(0, collection2026CaptainIndex + 1),
        { type: 'editorial-2026', id: 'collection-2026-editorial' },
        ...gridProducts.slice(collection2026CaptainIndex + 1),
      ]
    : gridProducts;
  let shopGridItems = gridProducts;
  if (isWomenView) {
    shopGridItems = mujerGridItems;
  } else if (isCollection2026View) {
    shopGridItems = collection2026GridItems;
  }

  useEffect(() => {
    if (!isCinematicView) {
      setIntroProgress(1);
      setIntroVisible(false);
      window.dispatchEvent(new CustomEvent('cinematicProgress', { detail: 1 }));
      return;
    }

    setIntroVisible(true);

    const handleScroll = () => {
      if (!introRef.current) return;
      const start = introRef.current.offsetTop;
      const end = start + introRef.current.offsetHeight - window.innerHeight;
      const progress = end > start ? (window.scrollY - start) / (end - start) : 1;
      const clamped = Math.min(1, Math.max(0, progress));
      setIntroProgress(clamped);
      if (videoRef.current) {
        const volume = Math.max(0, Math.pow(1 - clamped, 1.4));
        videoRef.current.volume = volume;
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isCinematicView]);

  useEffect(() => {
    if (!isCinematicView) return;
    window.dispatchEvent(new CustomEvent('cinematicProgress', { detail: introProgress }));
  }, [introProgress, isCinematicView]);

  useEffect(() => {
    if (!isCinematicView || !videoRef.current) return;
    const video = videoRef.current;
    video.muted = false;
    video.volume = 1;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {});
    }
  }, [isCinematicView]);

  const cinematicBackground = useMemo(() => {
    if (!isCinematicView) return undefined;
    const mix = (start, end, t) => Math.round(start + (end - start) * t);
    const t = Math.min(1, Math.max(0, introProgress));
    const r = mix(7, 255, t);
    const g = mix(8, 255, t);
    const b = mix(12, 255, t);
    return `rgb(${r}, ${g}, ${b})`;
  }, [introProgress, isCinematicView]);

  const overlayOpacity = isCinematicView ? 0.75 * (1 - introProgress) : 0;
  const videoScale = 1 - introProgress * 0.06;
  const videoOpacity = introVisible ? 1 : 0;
  const productsReveal = isCinematicView ? introProgress : 1;
  const cinematicVideoSrc = isMensView ? HOMBRE_CINEMATIC_VIDEO : MUJER_CINEMATIC_VIDEO;

  return (
    <div
      data-testid="shop-page"
      className="min-h-screen pt-32 md:pt-40 pb-24 noise-overlay editorial-noise"
      style={cinematicBackground ? { backgroundColor: cinematicBackground } : undefined}
    >
      {isCinematicView && (
        <div
          data-testid="category-cinematic-overlay"
          className="fixed inset-0 pointer-events-none transition-opacity duration-500"
          style={{ backgroundColor: 'rgba(5, 6, 10, 1)', opacity: overlayOpacity, zIndex: 5 }}
        />
      )}
      {isCinematicView && (
        <div
          ref={introRef}
          data-testid="category-cinematic-intro"
          className="relative w-screen max-w-none left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-32 md:-mt-40 h-[calc(100vh+40px)]"
        >
          <div className="sticky top-0 h-screen flex items-center justify-center bg-[#06070C] relative z-[20]">
            <div className="relative z-[40] flex items-center justify-center w-full h-full">
              <div
                className="w-[min(88vw,960px)] aspect-[4/3] flex items-center justify-center transition-[transform,opacity] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                style={{ transform: `scale(${videoScale})`, opacity: videoOpacity }}
              >
                <video
                  ref={videoRef}
                  data-testid="category-cinematic-video"
                  src={cinematicVideoSrc}
                  autoPlay
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-auto object-contain object-center rounded-[22px] shadow-[0_30px_80px_rgba(0,0,0,0.35)]"
                />
              </div>
            </div>
          </div>
        </div>
      )}
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-24">
        {isCollection2026View && (
          <CategoryHeaderMedia
            type="video"
            src={COLLECTION_2026_HEADER_VIDEO}
            alt="Editorial 2026 Header TAMBVRINI"
            testId="editorial-2026-header"
          />
        )}
        {isAccessoriesView && (
          <CategoryHeaderMedia
            type="image"
            src={ACCESSORIES_HEADER_IMAGE}
            alt="Editorial Accesorios Header TAMBVRINI"
            testId="editorial-accesorios-header"
          />
        )}

        {/* Header */}
        <div className="relative flex flex-col md:flex-row justify-start items-start md:items-end mt-20 md:mt-24 mb-10 md:mb-14 gap-6">
          <div className="w-full text-left">
            <h1 data-testid="shop-title" className="font-cinzel text-3xl md:text-4xl lg:text-5xl tracking-[0.12em] text-editorial-red font-normal">{getTitle()}</h1>
            <p className="font-montserrat text-xxs md:text-xs text-obsidian/40 mt-2 tracking-[0.12em]">
              {displayTotal} {displayTotal === 1 ? 'producto' : 'productos'}
            </p>
          </div>
          <div className="flex items-center gap-4 md:absolute md:right-0 md:bottom-0">
            {hasActiveFilters && (
              <button
                data-testid="clear-filters-btn"
                onClick={clearFilters}
                className="flex items-center gap-2 font-montserrat text-[10px] tracking-widest uppercase text-obsidian/50 hover:text-obsidian transition-colors duration-300"
              >
                <X size={12} /> Limpiar filtros
              </button>
            )}
            <Select value={sort} onValueChange={(v) => { const p = new URLSearchParams(searchParams); p.set('sort', v); setSearchParams(p); }}>
              <SelectTrigger data-testid="sort-select" className="w-[200px] bg-transparent border-black/10 text-obsidian/60 font-montserrat text-xs">
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent className="bg-white border-black/10">
                <SelectItem value="newest" className="text-obsidian/80 font-montserrat text-xs">Más recientes</SelectItem>
                <SelectItem value="price_asc" className="text-obsidian/80 font-montserrat text-xs">Precio: menor a mayor</SelectItem>
                <SelectItem value="price_desc" className="text-obsidian/80 font-montserrat text-xs">Precio: mayor a menor</SelectItem>
                <SelectItem value="name" className="text-obsidian/80 font-montserrat text-xs">Nombre A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Category pills */}
        <div className="flex flex-wrap gap-2 mb-12">
          {CATEGORY_NAV_ITEMS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => {
                if (key === 'limited-editions') {
                  navigate('/limited-editions');
                  return;
                }
                if (key === 'sport-club') {
                  navigate('/sport-club');
                  return;
                }
                const p = new URLSearchParams();
                if (key === 'hombre' || key === 'mujer') p.set('gender', key);
                else p.set('category', key);
                setSearchParams(p);
              }}
              className={`font-montserrat text-[10px] tracking-[0.15em] uppercase py-2 px-5 border transition-colors duration-300 ${
                normalizedCategory === key || gender === key
                  ? 'border-gold text-gold'
                  : 'border-black/10 text-obsidian/50 hover:text-obsidian hover:border-black/30'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Products grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-14">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-white/5 mb-4" />
                <div className="h-3 bg-white/5 w-2/3 mb-2" />
                <div className="h-3 bg-white/5 w-1/3" />
              </div>
            ))}
          </div>
        ) : gridProducts.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-playfair text-xl text-obsidian/50">No se encontraron productos</p>
            <button onClick={clearFilters} className="mt-6 btn-luxury text-xs">Ver todos los productos</button>
          </div>
        ) : isMensView && filteredProducts.length > 4 ? (
          <div
            className="space-y-10 md:space-y-14"
            style={
              isCinematicView
                ? {
                    opacity: productsReveal,
                    transform: `translateY(${(1 - productsReveal) * 24}px)`,
                    transition: 'opacity 500ms ease-out, transform 800ms cubic-bezier(0.22,1,0.36,1)',
                  }
                : undefined
            }
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-14">
              {mensFirst.map((p, i) => (
                <ProductCard
                  key={p.product_id}
                  product={p}
                  index={i}
                enableHoverVideo={supportsHoverVideo(p.product_id)}
                />
              ))}
            </div>
            <div className="w-screen max-w-none relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]">
              <img
                data-testid="mens-campaign-image"
                src={HOMBRE_CAMPAIGN_IMAGE}
                alt="Campaña Hombre TAMBVRINI"
                className="w-full h-auto object-contain object-center"
                loading="lazy"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-14">
              {mensRest.map((p, i) => (
                <ProductCard
                  key={p.product_id}
                  product={p}
                  index={i + mensFirst.length}
                  enableHoverVideo={supportsHoverVideo(p.product_id)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-14"
            style={
              isWomenView
                ? {
                    opacity: productsReveal,
                    transform: `translateY(${(1 - productsReveal) * 24}px)`,
                    transition: 'opacity 500ms ease-out, transform 800ms cubic-bezier(0.22,1,0.36,1)',
                  }
                : undefined
            }
          >
            {shopGridItems.map((item, i) => (
              item?.type === 'editorial' ? (
                <div
                  key={item.id}
                  data-testid="mujer-editorial-insert"
                  className="col-span-2 md:col-span-3 lg:col-span-2"
                >
                  <div className="relative aspect-[16/9] overflow-hidden rounded-[22px] shadow-[0_18px_46px_rgba(0,0,0,0.08)] transition-transform duration-300 ease-out hover:scale-[1.01] flex items-center justify-center">
                    <img
                      data-testid="mujer-editorial-image"
                      src={MUJER_HERO_IMAGE}
                      alt="Editorial Mujer TAMBVRINI"
                      loading="lazy"
                      className="w-full h-auto max-h-full object-contain object-center"
                    />
                  </div>
                </div>
              ) : item?.type === 'editorial-2026' ? (
                <div
                  key={item.id}
                  data-testid="editorial-2026-insert"
                  className="col-span-1 lg:row-span-2"
                >
                  <div className="h-full min-h-[22rem] lg:min-h-0 relative overflow-hidden rounded-[22px] shadow-[0_18px_46px_rgba(0,0,0,0.08)]">
                    <img
                      data-testid="editorial-2026-image"
                      src={COLLECTION_2026_EDITORIAL_IMAGE}
                      alt="Editorial 2026 TAMBVRINI"
                      loading="lazy"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
              ) : (
                <ProductCard
                  key={item.product_id}
                  product={item}
                  index={i}
                  enableHoverVideo={supportsHoverVideo(item.product_id)}
                />
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
